import { useEffect, useState, useMemo } from 'react';
import { useTimezoneSelect, allTimezones } from 'react-timezone-select';
import SignupComponent from '../../components/SignupComponent';
import LoginComponent from '../../components/LoginComponent';
import ProductQuestions from './ProductQuestions';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../utils/Auth';
import API from '../../API';
import { getDoc, doc } from 'firebase/firestore';
import { firestore } from '../../firebase';
import { sanitizeQrForClient } from '../../utils/sanitizeQrForClient';
import LoadingSpinner from '../../components/LoadingSpinner';
import styles from './SetUp.module.css';

export default function SetUp() {
    // ----- STATE INITIALIZATION -----
    const { userObj, orgObj, activeOrgId, currentUser, liquidQRs, create, login, optionsDispatch, isLoading } = useAuth();
    const params = useParams();
    const navigate = useNavigate();
    const { addUIDtoDB } = API;

    const [ authTypeBool, setAuthTypeBool ] = useState(true);
    const [ loading, setLoading ] = useState();
    const [ QR, setQR ] = useState({});
    const [ stickerId ] = useState(params.id);
    const [ qrLoad, setQrLoad ] = useState(true);
    const [ email, setEmail ] = useState('');
    const [ password, setPassword ] = useState('');
    const [ timezone ] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
    const [ formattedTimezone, setFormattedTimezone ] = useState({});
    const { options } = useTimezoneSelect('original', allTimezones);
    const currentRetailQrs = useMemo(() => liquidQRs?.filter(qr => qr.id.startsWith('-'))?.length || 0, [liquidQRs]);
    const canAddRetail = useMemo(() => (orgObj?.plan?.plan_id !== 0 || (orgObj?.plan?.plan_id === 0 && currentRetailQrs < 125)) ? true : false, [orgObj?.plan?.plan_id, currentRetailQrs]);
    // ----- END STATE INITIALIZATION -----
    // ----------------------------------------------

    useEffect(() => {
        if (timezone) {
            options.forEach(option => {
                if (option.value === timezone) {
                    setFormattedTimezone({ label: option.label, offset: option.offset, value: option.value });
                }
            });
        }
    }, [timezone, options]);

    useEffect(() => {
        let mounted = true;

        if (stickerId) {
            const getQrFromDatabase = async () => {
                // Check codes collection first for initialized document
                const codesSnap = await getDoc(doc(firestore, 'codes', stickerId));
                if (codesSnap.exists() && codesSnap.data()?.org_id) {
                    // Already initialized
                    if (mounted) {
                        setQR({ id: stickerId, data: sanitizeQrForClient(codesSnap.data()) });
                        setQrLoad(false);
                    }
                    return;
                }

                // Fall back to retail (staged) collection
                const stagedSnap = await getDoc(doc(firestore, 'retail', stickerId));
                if (stagedSnap.exists() && !stagedSnap.data().date_initialized) {
                    const staged = stagedSnap.data();
                    
                    // Normalize to the shape expected downstream
                    if (mounted) {
                        setQR({ id: stickerId, data: { product_id: staged.product_id || null }});
                        setQrLoad(false);
                    };
                    return
                }

                // Throw QR not found
                if (mounted) {
                    setQrLoad(false);
                    setQR(null);
                }
            };

            getQrFromDatabase();
            optionsDispatch({ type: 'liquid', options: { randKey: stickerId } });
        };

        return () => {
            mounted = false;
        }
    }, [stickerId, optionsDispatch]);
    // ----- END HELPER FUNCTIONS -----
    // ----------------------------------------------

    // ----- LOGIN FUNCTION -----
    const handleLogin = async () => {
        try {
            setLoading(true);
            await login(email, password);
        } catch (err) {
            console.error('Error logging in on SignUp: ', err);
        } finally {
            setLoading(false);
        };
    };
    // ----- END LOGIN FUNCTION -----
    // ----------------------------------------------

    // ----- SIGNUP FUNCTIONS -----
    const handleSignUp = async () => {
        try {
            const { user } = await create(email, password);

            const uid = user.uid || "";
            const data = {
                categories: [],
                team: [uid],
                plan: {
                    analytics: 0,
                    categories: 3,
                    liquid_qrs: 3,
                    plan_id: 0,
                    support: 0,
                    team_size: 1,
                    widgets: 0,
                },
                sonar_length: 3600000,
                timezone: formattedTimezone
            };

            await addUIDtoDB(data);
        } catch (error) {
            console.error("Error creating user from SetUp page", error.message);
        };
    };

    const loginRedirect = () => {
        const signupBtn = document.getElementById('signup-btn');
        signupBtn.classList.remove('setup-btn-clicked');
        setAuthTypeBool(true);
    };
    // ----- END SETUP FUNCTIONS -----
    // ----------------------------------------------

    // Derived state flags to simplify render branches
    const isAuthed = Boolean(currentUser && orgObj);
    const qrLoaded = !qrLoad && QR;
    const qrMissing = !qrLoad && !QR;
    const qrInitialized = qrLoaded && QR.data?.org_id;
    const qrInitializedByUser = qrInitialized && QR.data.org_id === activeOrgId;
    const qrIsRetail = qrLoaded && !qrInitialized && QR.data?.product_id;
    const limitReached = !canAddRetail;

    // ----- RETURN CONDITIONALS -----
    const renderAuthed = () => {
        if (limitReached) {
            return (
                <h3>
                    QR limit reached. <button onClick={() => navigate('/organizations/settings')} style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>Upgrade your account</button> to add more stickers.
                </h3>
            );
        }

        if (!qrLoaded) return <LoadingSpinner />;

        if (!qrInitialized) {
            if (qrIsRetail) return <ProductQuestions QR={QR} id={stickerId} />;
            return <RedirectWithSpinner to={`/collection/${stickerId}/edit`} />;
        }

        if (qrInitializedByUser) return <RedirectWithSpinner to={`/collection/${stickerId}/details`} />;

        return (
            <div className='d-flex justify-content-center mt-3'>
                <h4>QR has already been initialized. Click <Link to='/collection'>here</Link> to view your collection.</h4>
            </div>
        );
    };

    const renderUnauthed = () => (
        authTypeBool
            ? <LoginComponent handleLogin={handleLogin} setUp={true} loading={loading} email={email} setEmail={setEmail} password={password} setPassword={setPassword} />
            : <SignupComponent loginRedirect={loginRedirect} handleSignUp={handleSignUp} setUp={true} email={email} setEmail={setEmail} password={password} setPassword={setPassword} />
    );

    const handleReturn = () => {
        if (isLoading || loading) return <LoadingSpinner />;
        if (currentUser && qrMissing) {
            return (<h3>QR does not exist. Scan QR and follow the instructions on screen.</h3>);
        }
        if (isAuthed) return renderAuthed();
        return renderUnauthed();
    };
    // ----- END RETURN CONDITIONALS -----
    // ----------------------------------------------

    const RedirectWithSpinner = ({ to }) => {
        const navigate = useNavigate();
        useEffect(() => {
            let mounted = true;
            if (mounted) navigate(to, { replace: true });
            return () => { mounted = false };
        }, [to]);
        return <LoadingSpinner />;
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles.container}>
                {(isLoading || loading) ? 
                    <LoadingSpinner />
                : (!currentUser && !userObj) ?
                    <div style={{width: '100%'}}>
                        <div className='d-flex justify-content-center'>
                            <h2>Register QR</h2>
                        </div>
                        <div className={styles.inputGroup} style={{justifyContent: 'center', marginBottom: '1.5rem'}} >
                            <input type='radio' className={styles.radio} onChange={() => setAuthTypeBool(true)} checked={authTypeBool === true} id='login-radio' />
                            <label htmlFor='login-radio' className={styles.radioLabel}>Log In</label>
                            <input type='radio' className={styles.radio} onChange={() => setAuthTypeBool(false)} checked={authTypeBool === false} id='signup-radio'/>
                            <label htmlFor='signup-radio' className={styles.radioLabel}>Sign Up</label>
                        </div>
                    </div>
                :
                    <></>
                }
                {handleReturn()}
            </div>
        </div>
    );
};
